import {
  ChipCategoryCountRequest,
  ChipCategoryCountRequestV2,
} from '../../models/ChipCategoryCountRequest';
import { MedicalResourceRequest } from '../../models/MedicalResourceRequest';
import { CommonPesRequest } from '../../models/pes_api/CommonPesRequest';
import { ProviderSearchRequest } from '../../models/ProviderSearchRequest';
import { SnackCardRequest } from '../../models/SnackCardRequest';
import { APIConstants } from '../Constants';

export const parseLOB = (lob) =>
  APIConstants.OBAPI.LINEOFBUSINESS[`${lob}`] || APIConstants.OBAPI.ENI;

export const normalizeProviderId = (providerId: string) =>
  providerId?.split('NET')?.[0];

export const getLatLongParams = (latitude, longitude) =>
  `latitude=${latitude || ''}&longitude=${longitude || ''}`;

export const getLobParams = (lob) => `&lob=${parseLOB(lob)}`;

export const getPopulationParams = (population: string) =>
  `&population=${population}`;

export const getCoveragesParams = (coverageType) =>
  coverageType ? `&coverages=${coverageType}` : '';

export const getCarePathStepCategoryParams = (carePathStepCategory) =>
  carePathStepCategory ? `&carePathStepCategory=${carePathStepCategory}` : '';

export const getProviderLocationKeyParams = (providerLocationKey) =>
  providerLocationKey ? `&providerLocationKey=${providerLocationKey}` : '';

export const getPlanYearParams = (planYear) =>
  planYear ? `&planYear=${planYear}` : '';

export const getMemberEffectiveDateParams = (memberEffectiveDate) =>
  memberEffectiveDate ? `&memberEffectiveDate=${memberEffectiveDate}` : '';

export const getPlanVariationCodeParams = (planVariationCode) =>
  planVariationCode ? `&planVariationCode=${planVariationCode}` : '';

export const getPolicyIdParams = (policyID) =>
  policyID ? `&policyID=${policyID}` : '';

export const getProviderTypeParams = (providerType) =>
  `&providerType=${providerType}`;

export const getReciprocityIdParams = (reciprocityId) =>
  reciprocityId ? `&reciprocityId=${reciprocityId}` : '';

export const getSnpTypeParams = (snpType) =>
  snpType ? `&snpType=${snpType}` : '';

export const getRulesPackageKeyParams = (rulesPackageKey) =>
  rulesPackageKey ? `&rulesPackageKey=${rulesPackageKey}` : '';

export const getMedicalGroupAffiliationProviderIdParams = (
  medicalGroupAffiliationProviderId
) =>
  medicalGroupAffiliationProviderId
    ? `&medicalGroupAffiliationProviderId=${normalizeProviderId(
        medicalGroupAffiliationProviderId
      )}`
    : '';

export const getPaginationParams = (pageNumber, pageSize) =>
  `&pageSize=${pageSize}${pageNumber ? `&pageNumber=${pageNumber}` : ''}`;

export const getSortOrderParams = (sortOrder) =>
  sortOrder ? `&sortOrder=${sortOrder}` : '';

export const getProviderNameParams = (providerName) =>
  providerName ? `&providerName=${providerName}` : '';

export const getSpecialtyRollUpCodesParams = (specialtyRollUpCodes) =>
  specialtyRollUpCodes
    ? `&includeSpecialtyRollUpCodes=${specialtyRollUpCodes}`
    : '';

export const getSpecOrgCodesParams = (specOrgCodes) =>
  specOrgCodes ? `&includeSpecOrgCodes=${specOrgCodes}` : '';

export const getIsOxfordAndUSP = (
  claimSystemTypeCode?: string,
  eligibilitySystemTypeCode?: string,
  membershipCategory?: string,
  population?: string
) => {
  let isUSPMember: boolean = false;
  let isOXMember: boolean = false;
  if (
    (eligibilitySystemTypeCode?.toUpperCase() === 'CR' &&
      claimSystemTypeCode?.toUpperCase() === 'CR') ||
    ['USP', 'ENI_NICE'].includes(population?.toUpperCase() || '')
  ) {
    isUSPMember = true;

    if (membershipCategory?.toUpperCase() === 'OXFORD') {
      isOXMember = true;
    }
  }

  return {
    isUSPMember,
    isOXMember,
  };
};

export const getOxfordAndUSPQueryParams = (
  claimSystemTypeCode: string,
  eligibilitySystemTypeCode: string,
  membershipCategory: string,
  population: string
): string => {
  const { isUSPMember, isOXMember } = getIsOxfordAndUSP(
    claimSystemTypeCode,
    eligibilitySystemTypeCode,
    membershipCategory,
    population
  );

  return `&isUSPMember=${isUSPMember}&isOXMember=${isOXMember}`;
};

export const addOxfordAndUSPPostParams = (
  bodyObject:
    | ProviderSearchRequest
    | SnackCardRequest
    | ChipCategoryCountRequest
    | ChipCategoryCountRequestV2
    | MedicalResourceRequest,
  claimSystemTypeCode: string,
  eligibilitySystemTypeCode: string,
  membershipCategory: string,
  population: string
) => {
  const { isUSPMember, isOXMember } = getIsOxfordAndUSP(
    claimSystemTypeCode,
    eligibilitySystemTypeCode,
    membershipCategory,
    population
  );

  bodyObject.isUSPMember = isUSPMember;
  bodyObject.isOXMember = isOXMember;
};

export const getIsGuestUser = (isGuestUser) =>
  isGuestUser ? `&isGuestUser=${isGuestUser}` : '';

export const getLocationId = (locationId) =>
  locationId ? `&locationId=${locationId}` : '';

export const validateInputParams = (
  providerId: string,
  latitude: string,
  longitude: string
) => {
  if (providerId === '' && (latitude === '' || longitude === '')) {
    throw new Error('Missing required parameters.');
  }
};

export const getOrgTypeCodes = (codes, queryParam) => {
  let queryCode = '';
  codes?.forEach((code, idx) => {
    queryCode += `&${queryParam}[${idx}]=${code}`;
  });
  return queryCode;
};

export const getBaseRequestBody = (
  args: any,
  coverageType: string,
  stateCode: string,
  situsState: string,
  policyID: string,
  checkOon: boolean = false
): CommonPesRequest => {
  const { lob, providerType, reciprocityId, ...rest } = args;

  const decodedReciprocityId = reciprocityId
    ? decodeURIComponent(reciprocityId)
    : reciprocityId;

  const parsedLob = parseLOB(lob);

  return {
    ...rest,
    stateCode: stateCode?.toUpperCase(),
    situsState: situsState?.toUpperCase(),
    lob: parsedLob,
    coverages: [coverageType],
    policyID,
    checkOon: checkOon === true ? checkOon : undefined,
    providerType: providerType?.toUpperCase(),
    reciprocityId: decodedReciprocityId,
  };
};

export const getIncludeSpecialityRollupCodes = (code, rollupCodeMap) => {
  const codesArray = Array.isArray(code) ? code : [code];
  const updatedCodes: string[] = codesArray?.map((cd) => {
    const individualCodes = cd.split(',');
    const updatedIndividualCodes = individualCodes?.map((individualCode) =>
      rollupCodeMap === null || rollupCodeMap === undefined
        ? individualCode?.trim()
        : rollupCodeMap[individualCode?.trim()] || individualCode?.trim()
    );
    return updatedIndividualCodes?.join(', ');
  });

  return updatedCodes.filter((x) => x);
};

export const getHealthGradeRating = (healthGradeRating) => {
  if (!healthGradeRating) {
    return null;
  }

  const parsedRating = parseInt(healthGradeRating, 10);
  if (Number.isNaN(parsedRating)) {
    return null;
  }

  return parsedRating === 5 ? parsedRating.toString() : `${parsedRating}*`;
};

export const getIsCns = (claimSystemTypeCode, eligibilitySystemTypeCode) =>
  claimSystemTypeCode === 'A9' && eligibilitySystemTypeCode === 'A9';
